// Thin fetch wrapper. The NestJS API lives under /tradingview/api/* in prod
// (nginx routes there), and Vite dev-proxies the same prefix to localhost:3101.
// Admin auth = HTTP Basic; we attach the stored base64 credential as an
// Authorization header on every call so sessions survive browser restarts
// (see lib/auth.ts). A 401 clears the token and bounces back to the login
// gate.
import { clearAuth, getAuthToken } from "./auth";

const BASE = "/tradingview";

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getAuthToken();
  const authHeader: Record<string, string> = token
    ? { authorization: `Basic ${token}` }
    : {};
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: {
      "content-type": "application/json",
      ...authHeader,
      ...(init?.headers ?? {}),
    },
    ...init,
  });
  if (!res.ok) {
    if (res.status === 401) {
      // Stored credential is wrong/expired — drop it and force re-login.
      clearAuth();
      window.dispatchEvent(new Event("elvis-auth-expired"));
    }
    const body = await res.text().catch(() => "");
    throw new ApiError(res.status, body.slice(0, 200) || res.statusText);
  }
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: "GET" }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body == null ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body: body == null ? undefined : JSON.stringify(body) }),
};
